/* eslint-disable complexity */
import { Dispatch, SetStateAction, useState } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import { ActivityCard } from '@care/provider-react-components';
import { Box } from '@mui/material';
import { seoActivitySegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_activitySearchResults_seoSegmentationActivity as SEOSegmentationActivity } from '@/__generated__/seoActivitySegmentationListingPage';
import { useResponsive } from '@/components/hooks/useResponsive';
import {
  getActivityCardImages,
  getActivityCardProvider,
  getActivityCardRating,
  getActivityCardUrl,
} from '../helpers';

export type ActivitiesMarkerProps = {
  activity: SEOSegmentationActivity;
  id: number;
  setSelectedMarkerId: Dispatch<SetStateAction<number | null>>;
  slotProps?: { 'data-testid': string };
};

const ActivitiesMarker = ({
  activity,
  id,
  setSelectedMarkerId,
  slotProps,
}: ActivitiesMarkerProps) => {
  const { isSmallViewport } = useResponsive();
  const [selectedMarker, setSelectedMarker] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const { title, discount, price, address } = activity;
  const url = getActivityCardUrl(activity);

  const { latitude, longitude } = address ?? {};

  const currentPrice =
    Number(discount?.price?.amount) > 0 ? Number(discount?.price?.amount) : Number(price?.amount);
  const previousPrice =
    Number(discount?.price?.amount) > 0 ? Number(price?.amount) : Number(discount?.price?.amount);

  const handleMouseOver = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setSelectedMarker(true);
  };

  const handleMouseOut = () => {
    setTimeoutId(
      setTimeout(() => {
        setSelectedMarker(false);
      }, 500)
    );
  };

  // specific to mobile viewports to render info card on click
  const handleMouseClick = () => {
    setSelectedMarkerId(id);
  };

  const markerPropsMobile = {
    onClick: handleMouseClick,
  };

  const markerPropsTabletOrDesktop = {
    onClick: handleMouseOver,
    onMouseOver: handleMouseOver,
    onMouseOut: handleMouseOut,
  };

  const additionalMarkerProps = isSmallViewport ? markerPropsMobile : markerPropsTabletOrDesktop;

  return latitude && longitude ? (
    <>
      <Marker
        position={{ lat: latitude, lng: longitude }}
        title={title}
        {...additionalMarkerProps}
        {...slotProps}
      />
      {!isSmallViewport && selectedMarker && (
        <InfoWindow
          position={{ lat: latitude, lng: longitude }}
          onCloseClick={() => setSelectedMarker(false)}
          options={{
            ariaLabel: title,
          }}>
          <Box overflow="hidden" onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
            <ActivityCard
              title={title}
              prevPrice={previousPrice || undefined}
              price={currentPrice === 0 ? undefined : currentPrice}
              organizatorName={getActivityCardProvider(activity)}
              rating={getActivityCardRating(activity)}
              imageUrls={getActivityCardImages(activity)}
              description=""
              statusText={currentPrice === 0 ? 'Check price' : undefined}
              statusTextSx={({ palette }) => ({
                textDecoration: 'underline',
                fontWeight: 400,
                color: palette.text.tertiary,
              })}
              hideFavoriteIcon
              {...(url && {
                linkProps: {
                  href: url,
                  target: '_blank',
                },
              })}
            />
          </Box>
        </InfoWindow>
      )}
    </>
  ) : null;
};

export { ActivitiesMarker };
